/**
 * IP 黑名单管理（P-23 衍生，owner 专属）。
 *
 * 后端在「全局限流超额 / 客户端接口刷屏 / 登录爆破」时**自动**把攻击源 IP 拉黑。
 * 本页是对应的人工通道：列出当前生效封禁、按 IP 解封。
 *
 * 为什么需要这个页面（也是常见的运维误判）：
 * - 服务器 IP 不会被误封。api 绑定在 127.0.0.1，仅宿主机 Caddy 能连；Caddy 的
 *   reverse_proxy 给请求打上 `X-Forwarded-For: 真实客户端IP`，后端 trustProxy 解析出
 *   的 `request.ip` 永远是真实访客，服务器/反代 IP 既不可能是 socket 对端、也不在 XFF 里。
 * - 但**自动封禁可能把管理员自己锁门外**（例如连错 5 次密码触发 30 分钟登录封禁），
 *   因此必须留人工解封入口；/security/* 端点本身被黑名单钩子放行，保证被封后仍能自助恢复。
 */
import { useCallback, useState } from 'react';
import { EmptyFirstRun, ErrorBlock, TableSkeleton } from '../components/DataState';
import { describeError } from '../api/client';
import {
  listIpBlacklist,
  unblockIp,
} from '../api/endpoints';
import { ipBanReasonLabel, type IpBlacklistEntry } from '../api/types';
import { useAsync } from '../lib/useAsync';
import { formatDateTime } from '../lib/format';
import { Icon } from '../icons/Icon';
import { ICON_SIZE } from '../tokens';
import { useAuth } from '../auth/AuthContext';

const MONO: React.CSSProperties = { fontFamily: 'var(--font-mono)' };

const BADGE_ACTIVE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-1)',
  color: 'var(--danger)',
  fontWeight: 'var(--weight-medium)',
};
const BADGE_EXPIRED: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-1)',
  color: 'var(--muted)',
  fontWeight: 'var(--weight-medium)',
};

export function SecurityPage() {
  const { currentUser } = useAuth();
  const isOwner = currentUser?.role === 'owner';

  const list = useAsync(useCallback(() => listIpBlacklist(), []), []);
  const [busyIp, setBusyIp] = useState<string | null>(null);
  const [confirmIp, setConfirmIp] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const rows: readonly IpBlacklistEntry[] =
    list.state.kind === 'ready' ? list.state.data : [];

  const handleUnblock = useCallback(
    async (ip: string) => {
      setBusyIp(ip);
      setNotice(null);
      try {
        await unblockIp(ip);
        setConfirmIp(null);
        setNotice(`已解封 ${ip}（该 IP 下次请求即放行；若再次触发限流会被重新拉黑）`);
        list.reload();
      } catch (error: unknown) {
        setNotice(`解封失败：${describeError(error, '解封 IP')}`);
      } finally {
        setBusyIp(null);
      }
    },
    [list],
  );

  if (!isOwner) {
    return (
      <div className="page-head">
        <div>
          <h1 className="page-head__title">IP 黑名单</h1>
          <p className="page-head__sub">
            IP 黑名单属于高危运维操作，仅 owner 角色可查看与解封。请用 owner 账号登录后再访问本页。
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-head__title">IP 黑名单</h1>
          <p className="page-head__sub">
            自动封禁来自限流超额 / 接口刷屏 / 登录爆破，默认 1 小时（登录爆破 30 分钟）。
          </p>
        </div>
        <div className="page-head__actions">
          <button
            type="button"
            className="btn"
            onClick={() => {
              setNotice(null);
              list.reload();
            }}
            disabled={list.state.kind === 'loading'}
          >
            <Icon name="refresh-cw" size={ICON_SIZE.inline} />
            刷新
          </button>
        </div>
      </div>

      <div className="notice notice--warn" role="note">
        <Icon name="shield-check" size={ICON_SIZE.inline} />
        <span>
          服务器与反代 IP <strong>不会被封</strong>——后端按 <code style={MONO}>X-Forwarded-For</code>{' '}
          解析真实客户端 IP。若自己被误封，可在此解封（<code style={MONO}>/security/*</code>{' '}
          端点已被黑名单钩子放行，被封后仍能自助恢复）。
        </span>
      </div>

      {notice !== null ? (
        <div className="notice notice--ok" role="status">
          <Icon name="circle-check" size={ICON_SIZE.inline} />
          <span>{notice}</span>
        </div>
      ) : null}

      {list.state.kind === 'loading' ? (
        <div className="table-frame">
          <table className="data-table">
            <thead>
              <tr>
                <th>IP 地址</th>
                <th>封禁原因</th>
                <th>封禁时间</th>
                <th>到期时间</th>
                <th>状态</th>
                <th aria-label="操作" />
              </tr>
            </thead>
            <TableSkeleton rows={3} columns={6} />
          </table>
        </div>
      ) : list.state.kind === 'failed' ? (
        <ErrorBlock message={`加载 IP 黑名单失败：${describeError(list.state.error, '加载 IP 黑名单')}`} onRetry={list.reload} />
      ) : rows.length === 0 ? (
        <EmptyFirstRun
          icon="ban"
          description="当前没有任何生效的 IP 封禁。触发限流或登录爆破的攻击源会自动出现在这里。"
        />
      ) : (
        <div className="table-frame">
          <table className="data-table">
            <thead>
              <tr>
                <th>IP 地址</th>
                <th>封禁原因</th>
                <th>封禁时间</th>
                <th>到期时间</th>
                <th>状态</th>
                <th aria-label="操作" />
              </tr>
            </thead>
            <tbody>
              {rows.map((entry) => (
                <tr key={entry.ip}>
                  <td>
                    <code style={MONO}>{entry.ip}</code>
                  </td>
                  <td>{ipBanReasonLabel(entry.reason)}</td>
                  <td>{formatDateTime(entry.created_at)}</td>
                  <td>{entry.expires_at === null ? '永久' : formatDateTime(entry.expires_at)}</td>
                  <td>
                    {entry.expired ? (
                      <span style={BADGE_EXPIRED}>
                        <Icon name="clock" size={ICON_SIZE.badge} />
                        已过期
                      </span>
                    ) : (
                      <span style={BADGE_ACTIVE}>
                        <Icon name="ban" size={ICON_SIZE.badge} />
                        生效中
                      </span>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                      {confirmIp === entry.ip ? (
                        <>
                          <button
                            type="button"
                            className="btn btn--danger btn--sm"
                            disabled={busyIp === entry.ip}
                            onClick={() => void handleUnblock(entry.ip)}
                          >
                            {busyIp === entry.ip ? '解封中…' : '确认解封'}
                          </button>
                          <button
                            type="button"
                            className="btn btn--quiet btn--sm"
                            disabled={busyIp === entry.ip}
                            onClick={() => setConfirmIp(null)}
                          >
                            取消
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="btn btn--sm"
                          onClick={() => setConfirmIp(entry.ip)}
                        >
                          <Icon name="unlock" size={ICON_SIZE.inline} />
                          解封
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
